import type { ReactNode } from 'react';

import { GlowCard } from './GlowCard.js';

interface StatTileProps {
  label: string;
  value: ReactNode;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  hint?: string;
}

/**
 * KPI tile — matches the Figma "card: stat tile" (label / value / delta) including
 * the surface drop-shadow. Label is sentence-case secondary text, value is the
 * display figure, delta is success/error tinted.
 */
export function StatTile({ label, value, delta, hint }: StatTileProps): JSX.Element {
  const deltaColor =
    delta?.direction === 'up'   ? 'var(--color-success)' :
    delta?.direction === 'down' ? 'var(--color-error)'   :
                                  'var(--text-muted)';
  return (
    <GlowCard style={{ boxShadow: 'var(--shadow-stat-tile)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text-secondary)',
        }}>
          {label}
        </span>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontSize: 36,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: 'var(--text-primary)',
          lineHeight: 1.1,
        }}>
          {value}
        </span>
        {(delta || hint) && (
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            {delta && (
              <span style={{ color: deltaColor, fontWeight: 500 }}>
                {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '◆'} {delta.value}
              </span>
            )}
            {hint && <span style={{ color: 'var(--text-muted)' }}>{hint}</span>}
          </span>
        )}
      </div>
    </GlowCard>
  );
}
